import React, { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, TrendingUp, Settings } from 'lucide-react';
import { NotificationService } from '@/services/notificationService';
import { supabase } from '@/integrations/supabase/client';

interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<string, number>;
}

interface Notification {
  id: string;
  user_id: string;
  organization_uuid: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read_at: string | null;
  created_at: string;
}

export const NotificationDashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState<NotificationStats>({ total: 0, unread: 0, byType: {} });
  const [loading, setLoading] = useState(true);
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    loadDashboardData();
    return setupRealtimeSubscription();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load notification statistics
      const notificationStats = await NotificationService.getNotificationStats();
      setStats(notificationStats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel(`notification-dashboard-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          if (newNotification.user_id === user.id) {
            setStats(prev => ({
              ...prev,
              total: prev.total + 1,
              unread: prev.unread + 1,
              byType: {
                ...prev.byType,
                [newNotification.type]: (prev.byType[newNotification.type] || 0) + 1,
              },
            }));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          if (updatedNotification.user_id === user.id) {
            // Recalculate stats
            loadDashboardData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };



  if (loading) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Popover open={openPopover === 'total'} onOpenChange={(open) => setOpenPopover(open ? 'total' : null)}>
          <PopoverTrigger asChild>
            <Card 
              className="flex flex-row items-center p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onMouseEnter={() => setOpenPopover('total')}
              onMouseLeave={() => setOpenPopover(null)}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-blue-100 dark:bg-blue-900/20 mr-3">
                <Bell className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">
                  {t('notifications.total', 'Total Notifications')}
                </p>
                <div className="text-xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground truncate">
                  {stats.unread} {t('notifications.unread', 'unread')}
                </p>
              </div>
            </Card>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-2">
              <h4 className="font-semibold text-blue-500 dark:text-blue-400">Total Notifications</h4>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">
                {stats.unread} unread notifications out of {stats.total} total
              </p>
              <Progress value={(stats.unread / Math.max(stats.total, 1)) * 100} className="w-full" />
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={openPopover === 'unread'} onOpenChange={(open) => setOpenPopover(open ? 'unread' : null)}>
          <PopoverTrigger asChild>
            <Card 
              className="flex flex-row items-center p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onMouseEnter={() => setOpenPopover('unread')}
              onMouseLeave={() => setOpenPopover(null)}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-orange-100 dark:bg-orange-900/20 mr-3">
                <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center text-orange-600 dark:text-orange-400">
                  {stats.unread}
                </Badge>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">
                  {t('notifications.unread', 'Unread')}
                </p>
                <div className="text-xl font-bold text-orange-600">{stats.unread}</div>
                <p className="text-xs text-muted-foreground truncate">
                  {stats.total > 0 ? Math.round((stats.unread / stats.total) * 100) : 0}% of total
                </p>
              </div>
            </Card>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-2">
              <h4 className="font-semibold text-orange-600 dark:text-orange-400">Unread Notifications</h4>
              <div className="text-2xl font-bold text-orange-600">{stats.unread}</div>
              <p className="text-sm text-muted-foreground">
                {stats.total > 0 ? Math.round((stats.unread / stats.total) * 100) : 0}% of all notifications are unread
              </p>
              <div className="text-xs text-muted-foreground">
                {stats.unread} out of {stats.total} notifications
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={openPopover === 'types'} onOpenChange={(open) => setOpenPopover(open ? 'types' : null)}>
          <PopoverTrigger asChild>
            <Card 
              className="flex flex-row items-center p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onMouseEnter={() => setOpenPopover('types')}
              onMouseLeave={() => setOpenPopover(null)}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-purple-100 dark:bg-purple-900/20 mr-3">
                <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">
                  {t('notifications.types', 'Notification Types')}
                </p>
                <div className="text-xl font-bold">{Object.keys(stats.byType).length}</div>
                <p className="text-xs text-muted-foreground truncate">
                  Different types
                </p>
              </div>
            </Card>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-2">
              <h4 className="font-semibold text-purple-600 dark:text-purple-400">Notification Types</h4>
              <div className="text-2xl font-bold">{Object.keys(stats.byType).length}</div>
              <p className="text-sm text-muted-foreground">
                Different types of notifications in your system
              </p>
              <div className="text-xs text-muted-foreground">
                Including: {Object.keys(stats.byType).slice(0, 3).join(', ')}
                {Object.keys(stats.byType).length > 3 && ' and more...'}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={openPopover === 'readRate'} onOpenChange={(open) => setOpenPopover(open ? 'readRate' : null)}>
          <PopoverTrigger asChild>
            <Card 
              className="flex flex-row items-center p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onMouseEnter={() => setOpenPopover('readRate')}
              onMouseLeave={() => setOpenPopover(null)}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-green-100 dark:bg-green-900/20 mr-3">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">
                  {t('notifications.readRate', 'Read Rate')}
                </p>
                <div className="text-xl font-bold">
                  {stats.total > 0 ? Math.round(((stats.total - stats.unread) / stats.total) * 100) : 0}%
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  Notifications read
                </p>
              </div>
            </Card>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-2">
              <h4 className="font-semibold text-green-600 dark:text-green-400">Read Rate</h4>
              <div className="text-2xl font-bold">
                {stats.total > 0 ? Math.round(((stats.total - stats.unread) / stats.total) * 100) : 0}%
              </div>
              <p className="text-sm text-muted-foreground">
                {stats.total - stats.unread} out of {stats.total} notifications have been read
              </p>
              <Progress value={stats.total > 0 ? ((stats.total - stats.unread) / stats.total) * 100 : 0} className="w-full" />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Notification Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.breakdown', 'Notification Breakdown')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.byType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium capitalize">
                    {type.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">
                    {count}
                  </Badge>
                  <div className="w-20 bg-gray-200 rounded-sm h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-sm"
                      style={{ width: `${(count / stats.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>


    </div>
  );
}; 